import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Send, Loader2, Trash2, Zap, StopCircle, Info, RefreshCw, CheckCircle2, XCircle, AlertCircle, ImagePlus, X, Wrench, ChevronDown, ChevronUp, Braces, Code, Copy, Check } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useListSSHInstances } from '@/service/gpu-instance/hook';
import TyphoonOCRPlayground from './TyphoonOCRPlayground';

const PROXY_BASE_URL = 'https://proxy-instance.float16.cloud';
const DEFAULT_VLLM_PORT = 3900;

// Example tool definitions for demonstration
const EXAMPLE_TOOLS = [
  {
    type: 'function' as const,
    function: {
      name: 'get_weather',
      description: 'Get the current weather in a given location',
      parameters: {
        type: 'object',
        properties: {
          location: {
            type: 'string',
            description: 'City and state, e.g., "San Francisco, CA" or "Bangkok, Thailand"',
          },
          unit: {
            type: 'string',
            enum: ['celsius', 'fahrenheit'],
            description: 'Temperature unit',
          },
        },
        required: ['location', 'unit'],
      },
    },
  },
  {
    type: 'function' as const,
    function: {
      name: 'calculate',
      description: 'Perform a mathematical calculation',
      parameters: {
        type: 'object',
        properties: {
          expression: {
            type: 'string',
            description: 'Mathematical expression to evaluate, e.g., "2 + 2" or "sqrt(16)"',
          },
        },
        required: ['expression'],
      },
    },
  },
  {
    type: 'function' as const,
    function: {
      name: 'search_database',
      description: 'Search for information in a database',
      parameters: {
        type: 'object',
        properties: {
          query: {
            type: 'string',
            description: 'Search query string',
          },
          limit: {
            type: 'integer',
            description: 'Maximum number of results to return',
          },
        },
        required: ['query'],
      },
    },
  },
];

interface ToolCall {
  id: string;
  type: 'function';
  function: {
    name: string;
    arguments: string;
  };
}

// Structured Output (Grammar) example schemas
type StructuredOutputMode = 'none' | 'json_object' | 'json_schema' | 'regex' | 'choice';

interface StructuredOutputExample {
  id: string;
  name: string;
  description: string;
  mode: StructuredOutputMode;
  schema?: object;
  regex?: string;
  choices?: string[];
  examplePrompt: string;
}

const STRUCTURED_OUTPUT_EXAMPLES: StructuredOutputExample[] = [
  {
    id: 'person_info',
    name: 'Person Info',
    description: 'Extract person information as structured JSON',
    mode: 'json_schema',
    schema: {
      type: 'object',
      properties: {
        name: { type: 'string', description: 'Full name of the person' },
        age: { type: 'integer', description: 'Age in years' },
        occupation: { type: 'string', description: 'Job or profession' },
        email: { type: 'string', format: 'email', description: 'Email address' },
      },
      required: ['name', 'age'],
    },
    examplePrompt: 'Extract info: John Smith is a 32-year-old software engineer at john.smith@example.com',
  },
  {
    id: 'sentiment',
    name: 'Sentiment Analysis',
    description: 'Analyze sentiment with score and explanation',
    mode: 'json_schema',
    schema: {
      type: 'object',
      properties: {
        sentiment: { type: 'string', enum: ['positive', 'negative', 'neutral'] },
        confidence: { type: 'number', minimum: 0, maximum: 1 },
        explanation: { type: 'string' },
      },
      required: ['sentiment', 'confidence'],
    },
    examplePrompt: 'Analyze sentiment: I absolutely love this product! Best purchase ever!',
  },
  {
    id: 'product_review',
    name: 'Product Review',
    description: 'Parse product review into structured format',
    mode: 'json_schema',
    schema: {
      type: 'object',
      properties: {
        product_name: { type: 'string' },
        rating: { type: 'integer', minimum: 1, maximum: 5 },
        pros: { type: 'array', items: { type: 'string' } },
        cons: { type: 'array', items: { type: 'string' } },
        recommendation: { type: 'boolean' },
      },
      required: ['product_name', 'rating', 'recommendation'],
    },
    examplePrompt: 'Parse review: The iPhone 15 Pro is amazing! 5 stars. Pros: great camera, fast chip. Cons: expensive, heavy. Highly recommended!',
  },
  {
    id: 'simple_json',
    name: 'Simple JSON',
    description: 'Force output to be valid JSON (no schema)',
    mode: 'json_object',
    examplePrompt: 'List 3 programming languages with their year of creation as JSON',
  },
  {
    id: 'email_regex',
    name: 'Email Pattern',
    description: 'Generate text matching email regex pattern',
    mode: 'regex',
    regex: '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}',
    examplePrompt: 'Generate a valid email address for a user named Alice Wong',
  },
  {
    id: 'yes_no',
    name: 'Yes/No Choice',
    description: 'Constrain output to specific choices',
    mode: 'choice',
    choices: ['Yes', 'No', 'Maybe'],
    examplePrompt: 'Is Python a compiled language?',
  },
  {
    id: 'rating_choice',
    name: 'Rating Choice',
    description: 'Constrain to rating values',
    mode: 'choice',
    choices: ['1', '2', '3', '4', '5'],
    examplePrompt: 'Rate this sentence on a scale of 1-5: "The movie was absolutely fantastic!"',
  },
];

interface ImageAttachment {
  type: 'image_url';
  image_url: {
    url: string; // base64 data URL
  };
}

interface TextContent {
  type: 'text';
  text: string;
}

type MessageContent = string | (TextContent | ImageAttachment)[];

interface Message {
  role: 'user' | 'assistant';
  content: MessageContent;
  // For display purposes - store raw image URLs
  images?: string[];
  // Tool calls from assistant
  tool_calls?: ToolCall[];
}

interface VLLMModel {
  id: string;
  object: string;
  created: number;
  owned_by: string;
}

type HealthStatus = 'checking' | 'healthy' | 'unhealthy' | 'unknown';

const VLLMPlayground = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const { data: instances } = useListSSHInstances();
  const instance = instances?.find((i) => i.task_id === instanceId);

  // Get parent_task_id for consistent endpoint
  const parentTaskId = instance?.parent_task_id || instance?.task_id;

  // Check if vision is supported based on use_case
  const supportsVision = instance?.use_case?.includes('vision') ?? false;

  // Check if tools are supported based on use_case
  const supportsTools = instance?.use_case?.includes('tools') ?? false;

  // Check if Typhoon OCR mode
  const isTyphoonOCR = instance?.use_case?.includes('typhoon-ocr') ?? false;

  // All hooks must be declared before any conditional returns
  const [messages, setMessages] = useState<Message[]>([]);
  const [attachedImages, setAttachedImages] = useState<string[]>([]);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(512);
  const [streamEnabled, setStreamEnabled] = useState(true);
  const [port, setPort] = useState(DEFAULT_VLLM_PORT);
  const [selectedModel, setSelectedModel] = useState('');
  const [availableModels, setAvailableModels] = useState<VLLMModel[]>([]);
  const [healthStatus, setHealthStatus] = useState<HealthStatus>('unknown');
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Tool calling state
  const [toolsEnabled, setToolsEnabled] = useState(false);
  const [toolChoice, setToolChoice] = useState<'auto' | 'required' | 'none'>('auto');
  const [showToolsConfig, setShowToolsConfig] = useState(false);

  // Structured outputs (grammar) state
  const [grammarEnabled, setGrammarEnabled] = useState(false);
  const [selectedGrammarExample, setSelectedGrammarExample] = useState<string>('person_info');
  const [showGrammarConfig, setShowGrammarConfig] = useState(false);

  // Code example dialog state
  const [showCodeDialog, setShowCodeDialog] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  // Build proxy endpoint - format: baseUrl/task_id/port/path
  const proxyBase = `${PROXY_BASE_URL}/${parentTaskId}/${port}`;
  const chatCompletionsEndpoint = `${proxyBase}/v1/chat/completions`;
  const healthEndpoint = `${proxyBase}/health`;
  const modelsEndpoint = `${proxyBase}/v1/models`;

  // Check health status
  const checkHealth = useCallback(async () => {
    if (!parentTaskId) return;

    setHealthStatus('checking');
    try {
      const response = await fetch(healthEndpoint, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      });
      setHealthStatus(response.ok ? 'healthy' : 'unhealthy');
    } catch {
      setHealthStatus('unhealthy');
    }
  }, [healthEndpoint, parentTaskId]);

  // Fetch available models
  const fetchModels = useCallback(async () => {
    if (!parentTaskId) return;

    setIsLoadingModels(true);
    try {
      const response = await fetch(modelsEndpoint, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      });
      if (response.ok) {
        const data = await response.json();
        const models: VLLMModel[] = data.data || [];
        setAvailableModels(models);
        // Auto-select first model if none selected
        if (models.length > 0 && !selectedModel) {
          setSelectedModel(models[0].id);
        }
      }
    } catch {
      setAvailableModels([]);
    } finally {
      setIsLoadingModels(false);
    }
  }, [modelsEndpoint, parentTaskId, selectedModel]);

  // Refresh both health and models
  const refreshStatus = useCallback(async () => {
    await Promise.all([checkHealth(), fetchModels()]);
  }, [checkHealth, fetchModels]);

  // Check health and fetch models on mount and when port changes
  useEffect(() => {
    if (parentTaskId) {
      refreshStatus();
    }
  }, [parentTaskId, port, refreshStatus]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent]);

  // Build request body for code examples
  const buildExampleRequestBody = useCallback(() => {
    const exampleMessage = input.trim() || 'Hello, how are you?';
    const requestBody: Record<string, any> = {
      model: selectedModel || 'your-model-name',
      messages: [
        { role: 'user', content: exampleMessage }
      ],
      temperature,
      max_tokens: maxTokens,
      stream: streamEnabled && !toolsEnabled && !grammarEnabled,
    };

    // Add tools if enabled
    if (supportsTools && toolsEnabled) {
      requestBody.tools = EXAMPLE_TOOLS;
      requestBody.tool_choice = toolChoice;
      requestBody.stream = false;
    }

    // Add structured output (grammar) if enabled
    if (grammarEnabled) {
      const selectedExample = STRUCTURED_OUTPUT_EXAMPLES.find(e => e.id === selectedGrammarExample);
      if (selectedExample) {
        switch (selectedExample.mode) {
          case 'json_object':
            requestBody.response_format = { type: 'json_object' };
            break;
          case 'json_schema':
            requestBody.response_format = {
              type: 'json_schema',
              json_schema: {
                name: selectedExample.id,
                strict: true,
                schema: selectedExample.schema,
              },
            };
            break;
          case 'regex':
            requestBody.extra_body = { guided_regex: selectedExample.regex };
            break;
          case 'choice':
            requestBody.extra_body = { guided_choice: selectedExample.choices };
            break;
        }
        requestBody.stream = false;
      }
    }

    return requestBody;
  }, [input, selectedModel, temperature, maxTokens, streamEnabled, toolsEnabled, grammarEnabled, supportsTools, toolChoice, selectedGrammarExample]);

  // Generate Python code example
  const generatePythonCode = useCallback(() => {
    const requestBody = buildExampleRequestBody();
    const baseUrl = chatCompletionsEndpoint.replace('/v1/chat/completions', '/v1');

    let code = `from openai import OpenAI

client = OpenAI(
    base_url="${baseUrl}",
    api_key="not-needed"  # vLLM doesn't require API key
)

`;

    // Add tool definitions if tools are enabled
    if (supportsTools && toolsEnabled) {
      code += `# Tool definitions
tools = ${JSON.stringify(EXAMPLE_TOOLS, null, 4)}

`;
    }

    // Build the API call
    code += `response = client.chat.completions.create(
    model="${requestBody.model}",
    messages=${JSON.stringify(requestBody.messages, null, 8).replace(/\n/g, '\n    ')},
    temperature=${requestBody.temperature},
    max_tokens=${requestBody.max_tokens},
    stream=${requestBody.stream ? 'True' : 'False'}`;

    if (supportsTools && toolsEnabled) {
      code += `,
    tools=tools,
    tool_choice="${toolChoice}"`;
    }

    if (grammarEnabled && requestBody.response_format) {
      code += `,
    response_format=${JSON.stringify(requestBody.response_format, null, 8).replace(/\n/g, '\n    ')}`;
    }

    if (grammarEnabled && requestBody.extra_body) {
      code += `,
    extra_body=${JSON.stringify(requestBody.extra_body, null, 8).replace(/\n/g, '\n    ')}`;
    }

    code += `
)

`;

    // Add response handling
    if (supportsTools && toolsEnabled) {
      code += `# Handle tool calls
if response.choices[0].message.tool_calls:
    for tool_call in response.choices[0].message.tool_calls:
        print(f"Function: {tool_call.function.name}")
        print(f"Arguments: {tool_call.function.arguments}")
else:
    print(response.choices[0].message.content)`;
    } else if (grammarEnabled) {
      code += `# Structured output
import json
result = json.loads(response.choices[0].message.content)
print(json.dumps(result, indent=2))`;
    } else if (requestBody.stream) {
      code += `# Stream response
for chunk in response:
    if chunk.choices[0].delta.content:
        print(chunk.choices[0].delta.content, end="", flush=True)`;
    } else {
      code += `# Print response
print(response.choices[0].message.content)`;
    }

    return code;
  }, [buildExampleRequestBody, chatCompletionsEndpoint, supportsTools, toolsEnabled, toolChoice, grammarEnabled]);

  // Generate curl command
  const generateCurlCommand = useCallback(() => {
    const requestBody = buildExampleRequestBody();

    let curl = `curl -X POST "${chatCompletionsEndpoint}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(requestBody, null, 2).replace(/'/g, "'\\''")}'`;

    return curl;
  }, [buildExampleRequestBody, chatCompletionsEndpoint]);

  // Copy code to clipboard
  const handleCopyCode = useCallback(async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const handleSubmit = async () => {
    if (!input.trim() || isLoading || !parentTaskId) return;

    // Build message content - with or without images
    let messageContent: MessageContent;
    const currentImages = [...attachedImages];

    if (supportsVision && currentImages.length > 0) {
      // Vision format: array of content parts
      const contentParts: (TextContent | ImageAttachment)[] = [
        { type: 'text', text: input.trim() },
        ...currentImages.map((img) => ({
          type: 'image_url' as const,
          image_url: { url: img },
        })),
      ];
      messageContent = contentParts;
    } else {
      // Text-only format
      messageContent = input.trim();
    }

    const userMessage: Message = {
      role: 'user',
      content: messageContent,
      images: currentImages.length > 0 ? currentImages : undefined,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setAttachedImages([]);
    setIsLoading(true);
    setStreamingContent('');

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    // Format messages for API - keep vision format for messages with images
    const formattedMessages = [...messages, userMessage].map((m) => ({
      role: m.role,
      content: m.content,
    }));

    try {
      // Build request body
      const requestBody: Record<string, any> = {
        ...(selectedModel && { model: selectedModel }),
        messages: formattedMessages,
        temperature,
        max_tokens: maxTokens,
        stream: streamEnabled,
      };

      // Add tools if enabled
      if (supportsTools && toolsEnabled) {
        requestBody.tools = EXAMPLE_TOOLS;
        requestBody.tool_choice = toolChoice;
        // Disable streaming for tool calls to get complete response
        requestBody.stream = false;
      }

      // Add structured output (grammar) if enabled
      if (grammarEnabled) {
        const selectedExample = STRUCTURED_OUTPUT_EXAMPLES.find(e => e.id === selectedGrammarExample);
        if (selectedExample) {
          switch (selectedExample.mode) {
            case 'json_object':
              requestBody.response_format = { type: 'json_object' };
              break;
            case 'json_schema':
              requestBody.response_format = {
                type: 'json_schema',
                json_schema: {
                  name: selectedExample.id,
                  strict: true,
                  schema: selectedExample.schema,
                },
              };
              break;
            case 'regex':
              // vLLM extra parameter for regex-guided generation
              requestBody.extra_body = {
                ...requestBody.extra_body,
                guided_regex: selectedExample.regex,
              };
              break;
            case 'choice':
              // vLLM extra parameter for choice-guided generation
              requestBody.extra_body = {
                ...requestBody.extra_body,
                guided_choice: selectedExample.choices,
              };
              break;
          }
          // Disable streaming for structured outputs
          requestBody.stream = false;
        }
      }

      const response = await fetch(chatCompletionsEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }

      if (streamEnabled && !toolsEnabled && !grammarEnabled && response.body) {
        // Handle streaming response
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // Append new chunk to buffer
          buffer += decoder.decode(value, { stream: true });

          // Process complete lines from buffer
          const lines = buffer.split('\n');
          // Keep the last incomplete line in buffer
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmedLine = line.trim();
            if (!trimmedLine) continue;

            if (trimmedLine.startsWith('data: ')) {
              const data = trimmedLine.slice(6);
              if (data === '[DONE]') continue;

              try {
                const parsed = JSON.parse(data);
                const content = parsed.choices?.[0]?.delta?.content || '';
                if (content) {
                  fullContent += content;
                  setStreamingContent(fullContent);
                }
              } catch {
                // Skip invalid JSON
              }
            }
          }
        }

        // Process any remaining data in buffer
        if (buffer.trim()) {
          const trimmedLine = buffer.trim();
          if (trimmedLine.startsWith('data: ')) {
            const data = trimmedLine.slice(6);
            if (data !== '[DONE]') {
              try {
                const parsed = JSON.parse(data);
                const content = parsed.choices?.[0]?.delta?.content || '';
                if (content) {
                  fullContent += content;
                }
              } catch {
                // Skip invalid JSON
              }
            }
          }
        }

        if (fullContent) {
          setMessages((prev) => [...prev, { role: 'assistant', content: fullContent }]);
        }
        setStreamingContent('');
      } else {
        // Handle non-streaming response (including tool calls)
        const data = await response.json();
        const message = data.choices?.[0]?.message;
        const assistantContent = message?.content || '';
        const toolCalls = message?.tool_calls as ToolCall[] | undefined;

        if (toolCalls && toolCalls.length > 0) {
          // Response contains tool calls
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: assistantContent || 'Tool call requested:',
              tool_calls: toolCalls,
            },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', content: assistantContent || 'No response' },
          ]);
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        // Request was cancelled
        if (streamingContent) {
          setMessages((prev) => [...prev, { role: 'assistant', content: streamingContent + ' [stopped]' }]);
          setStreamingContent('');
        }
      } else {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: `Error: ${error.message}` },
        ]);
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setStreamingContent('');
    setAttachedImages([]);
  };

  // Handle image attachment
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach((file) => {
      if (!file.type.startsWith('image/')) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        setAttachedImages((prev) => [...prev, base64]);
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const removeAttachedImage = (index: number) => {
    setAttachedImages((prev) => prev.filter((_, i) => i !== index));
  };

  if (!instance || !parentTaskId) {
    return (
      <div className="container py-8">
        <Alert>
          <AlertDescription>Loading instance details...</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Render Typhoon OCR playground if use_case includes 'typhoon-ocr'
  if (isTyphoonOCR) {
    return <TyphoonOCRPlayground parentTaskId={parentTaskId} />;
  }

  return (
    <div className="container py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="h-6 w-6 text-yellow-500" />
            vLLM Playground
          </h1>
          <p className="text-muted-foreground mt-1">
            Test your deployed model with real-time inference
          </p>
        </div>
        <div className="flex items-center gap-2">
          {supportsVision && (
            <Badge variant="outline" className="text-cyan-600 border-cyan-500/30">
              <ImagePlus className="h-3 w-3 mr-1" />
              Vision
            </Badge>
          )}
          {supportsTools && (
            <Badge variant="outline" className={toolsEnabled ? "text-orange-600 border-orange-500/30" : "text-muted-foreground"}>
              <Wrench className="h-3 w-3 mr-1" />
              Tools
            </Badge>
          )}
          <Badge variant="outline" className={grammarEnabled ? "text-purple-600 border-purple-500/30" : "text-muted-foreground"}>
            <Braces className="h-3 w-3 mr-1" />
            Grammar
          </Badge>
          {selectedModel && (
            <Badge variant="secondary" className="font-mono text-xs">
              {selectedModel}
            </Badge>
          )}
          <Badge
            variant="outline"
            className={
              healthStatus === 'healthy'
                ? 'text-green-600 border-green-500/30'
                : healthStatus === 'unhealthy'
                ? 'text-red-600 border-red-500/30'
                : 'text-muted-foreground'
            }
          >
            {healthStatus === 'checking' && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
            {healthStatus === 'healthy' && <CheckCircle2 className="h-3 w-3 mr-1" />}
            {healthStatus === 'unhealthy' && <XCircle className="h-3 w-3 mr-1" />}
            {healthStatus === 'checking' ? 'Checking' : healthStatus}
          </Badge>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Settings Panel */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Health Status */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Server Status</Label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={refreshStatus}
                  disabled={healthStatus === 'checking' || isLoadingModels}
                  className="h-7 px-2"
                >
                  <RefreshCw className={`h-3 w-3 ${healthStatus === 'checking' ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                {healthStatus === 'checking' && (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Checking...</span>
                  </>
                )}
                {healthStatus === 'healthy' && (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-600">Healthy</span>
                  </>
                )}
                {healthStatus === 'unhealthy' && (
                  <>
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-600">Unhealthy</span>
                  </>
                )}
                {healthStatus === 'unknown' && (
                  <>
                    <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Unknown</span>
                  </>
                )}
              </div>
            </div>

            {/* Port Settings */}
            <div className="space-y-3">
              <Label>Port (3000-4000)</Label>
              <Input
                type="number"
                min={3000}
                max={4000}
                value={port}
                onChange={(e) => {
                  const p = parseInt(e.target.value);
                  if (p >= 3000 && p <= 4000) setPort(p);
                }}
                disabled={isLoading}
                className="font-mono"
              />
            </div>

            {/* Model Selector */}
            <div className="space-y-3">
              <Label>Model</Label>
              {isLoadingModels ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading models...
                </div>
              ) : availableModels.length > 0 ? (
                <Select value={selectedModel} onValueChange={setSelectedModel} disabled={isLoading}>
                  <SelectTrigger className="font-mono text-xs">
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableModels.map((model) => (
                      <SelectItem key={model.id} value={model.id} className="font-mono text-xs">
                        {model.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No models available
                </div>
              )}
            </div>

            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label>Temperature</Label>
                <span className="text-sm text-muted-foreground">{temperature}</span>
              </div>
              <Slider
                value={[temperature]}
                min={0}
                max={2}
                step={0.1}
                onValueChange={([v]) => setTemperature(v)}
                disabled={isLoading}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Max Tokens</Label>
                <span className="text-sm text-muted-foreground">{maxTokens}</span>
              </div>
              <Slider
                value={[maxTokens]}
                min={64}
                max={4096}
                step={64}
                onValueChange={([v]) => setMaxTokens(v)}
                disabled={isLoading}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Streaming</Label>
              <Switch
                checked={streamEnabled}
                onCheckedChange={setStreamEnabled}
                disabled={isLoading || toolsEnabled || grammarEnabled}
              />
            </div>

            {/* Tool Calling Section */}
            {supportsTools && (
              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Wrench className="h-4 w-4" />
                    Tool Calling
                  </Label>
                  <Switch
                    checked={toolsEnabled}
                    onCheckedChange={(v) => {
                      setToolsEnabled(v);
                      if (v) setGrammarEnabled(false); // Disable grammar when tools enabled
                    }}
                    disabled={isLoading || grammarEnabled}
                  />
                </div>

                {toolsEnabled && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-xs">Tool Choice</Label>
                      <Select value={toolChoice} onValueChange={(v) => setToolChoice(v as typeof toolChoice)} disabled={isLoading}>
                        <SelectTrigger className="text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto</SelectItem>
                          <SelectItem value="required">Required</SelectItem>
                          <SelectItem value="none">None</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div
                      className="flex items-center justify-between cursor-pointer text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => setShowToolsConfig(!showToolsConfig)}
                    >
                      <span>Available Tools ({EXAMPLE_TOOLS.length})</span>
                      {showToolsConfig ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </div>

                    {showToolsConfig && (
                      <div className="space-y-2 text-xs">
                        {EXAMPLE_TOOLS.map((tool) => (
                          <div key={tool.function.name} className="p-2 border rounded bg-muted/50">
                            <p className="font-mono font-medium">{tool.function.name}</p>
                            <p className="text-muted-foreground mt-1">{tool.function.description}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground">
                      Try: "What's the weather in Bangkok?" or "Calculate 25 * 4"
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Structured Outputs (Grammar) Section */}
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Braces className="h-4 w-4" />
                  Structured Output
                </Label>
                <Switch
                  checked={grammarEnabled}
                  onCheckedChange={(v) => {
                    setGrammarEnabled(v);
                    if (v) setToolsEnabled(false); // Disable tools when grammar enabled
                  }}
                  disabled={isLoading || toolsEnabled}
                />
              </div>

              {grammarEnabled && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Output Format</Label>
                    <Select value={selectedGrammarExample} onValueChange={setSelectedGrammarExample} disabled={isLoading}>
                      <SelectTrigger className="text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STRUCTURED_OUTPUT_EXAMPLES.map((example) => (
                          <SelectItem key={example.id} value={example.id} className="text-xs">
                            {example.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div
                    className="flex items-center justify-between cursor-pointer text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setShowGrammarConfig(!showGrammarConfig)}
                  >
                    <span>Schema Details</span>
                    {showGrammarConfig ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </div>

                  {showGrammarConfig && (
                    <div className="space-y-2 text-xs">
                      {(() => {
                        const example = STRUCTURED_OUTPUT_EXAMPLES.find(e => e.id === selectedGrammarExample);
                        if (!example) return null;
                        return (
                          <div className="p-2 border rounded bg-muted/50 space-y-2">
                            <p className="text-muted-foreground">{example.description}</p>
                            <Badge variant="outline" className="text-[10px]">
                              {example.mode === 'json_schema' && 'JSON Schema'}
                              {example.mode === 'json_object' && 'JSON Object'}
                              {example.mode === 'regex' && 'Regex Pattern'}
                              {example.mode === 'choice' && 'Choice'}
                            </Badge>
                            {example.schema && (
                              <pre className="text-[10px] font-mono bg-background/50 p-2 rounded overflow-x-auto max-h-32">
                                {JSON.stringify(example.schema, null, 2)}
                              </pre>
                            )}
                            {example.regex && (
                              <p className="font-mono bg-background/50 p-1 rounded text-[10px]">{example.regex}</p>
                            )}
                            {example.choices && (
                              <div className="flex flex-wrap gap-1">
                                {example.choices.map(c => (
                                  <Badge key={c} variant="secondary" className="text-[10px]">{c}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground">
                    Try: "{STRUCTURED_OUTPUT_EXAMPLES.find(e => e.id === selectedGrammarExample)?.examplePrompt}"
                  </p>
                </div>
              )}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={clearChat}
              disabled={isLoading || messages.length === 0}
              className="w-full"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Chat
            </Button>
          </CardContent>
        </Card>

        {/* Chat Panel */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Chat</CardTitle>
                <CardDescription>
                  Endpoint: <code className="text-xs">{chatCompletionsEndpoint}</code>
                </CardDescription>
              </div>
              <Dialog open={showCodeDialog} onOpenChange={setShowCodeDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Code className="h-4 w-4 mr-2" />
                    View Code
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
                  <DialogHeader>
                    <DialogTitle>API Request Example</DialogTitle>
                    <DialogDescription>
                      Copy the code below to integrate with your application
                    </DialogDescription>
                  </DialogHeader>
                  <Tabs defaultValue="python" className="flex-1 overflow-hidden flex flex-col">
                    <TabsList className="w-full justify-start">
                      <TabsTrigger value="python">Python</TabsTrigger>
                      <TabsTrigger value="curl">cURL</TabsTrigger>
                      <TabsTrigger value="json">Request Body</TabsTrigger>
                    </TabsList>
                    <TabsContent value="python" className="flex-1 overflow-hidden">
                      <div className="relative h-full">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="absolute top-2 right-2 z-10"
                          onClick={() => handleCopyCode(generatePythonCode())}
                        >
                          {codeCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                        <pre className="h-[400px] overflow-auto bg-muted p-4 rounded-lg text-xs font-mono">
                          {generatePythonCode()}
                        </pre>
                      </div>
                    </TabsContent>
                    <TabsContent value="curl" className="flex-1 overflow-hidden">
                      <div className="relative h-full">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="absolute top-2 right-2 z-10"
                          onClick={() => handleCopyCode(generateCurlCommand())}
                        >
                          {codeCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                        <pre className="h-[400px] overflow-auto bg-muted p-4 rounded-lg text-xs font-mono whitespace-pre-wrap">
                          {generateCurlCommand()}
                        </pre>
                      </div>
                    </TabsContent>
                    <TabsContent value="json" className="flex-1 overflow-hidden">
                      <div className="relative h-full">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="absolute top-2 right-2 z-10"
                          onClick={() => handleCopyCode(JSON.stringify(buildExampleRequestBody(), null, 2))}
                        >
                          {codeCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                        <pre className="h-[400px] overflow-auto bg-muted p-4 rounded-lg text-xs font-mono">
                          {JSON.stringify(buildExampleRequestBody(), null, 2)}
                        </pre>
                      </div>
                    </TabsContent>
                  </Tabs>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Messages */}
            <div className="h-[400px] overflow-y-auto border rounded-lg p-4 space-y-4 bg-muted/30">
              {messages.length === 0 && !streamingContent && (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                  Send a message to start the conversation
                </div>
              )}
              {messages.map((message, index) => {
                // Extract text content for display
                const textContent = typeof message.content === 'string'
                  ? message.content
                  : message.content.find((c) => c.type === 'text')?.text || '';

                return (
                  <div
                    key={index}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-4 py-2 ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      {/* Display attached images */}
                      {message.images && message.images.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {message.images.map((img, imgIndex) => (
                            <img
                              key={imgIndex}
                              src={img}
                              alt={`Attached ${imgIndex + 1}`}
                              className="max-w-[200px] max-h-[150px] rounded object-cover"
                            />
                          ))}
                        </div>
                      )}
                      {textContent && (
                        <p className="text-sm whitespace-pre-wrap">{textContent}</p>
                      )}
                      {/* Display tool calls */}
                      {message.tool_calls && message.tool_calls.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {message.tool_calls.map((toolCall) => {
                            let parsedArgs: Record<string, any> = {};
                            try {
                              parsedArgs = JSON.parse(toolCall.function.arguments);
                            } catch {
                              parsedArgs = { raw: toolCall.function.arguments };
                            }
                            return (
                              <div
                                key={toolCall.id}
                                className="border border-orange-500/30 bg-orange-500/10 rounded p-2"
                              >
                                <div className="flex items-center gap-2 text-xs font-medium text-orange-600 mb-1">
                                  <Wrench className="h-3 w-3" />
                                  Tool Call: {toolCall.function.name}
                                </div>
                                <pre className="text-xs font-mono bg-background/50 p-2 rounded overflow-x-auto">
                                  {JSON.stringify(parsedArgs, null, 2)}
                                </pre>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {streamingContent && (
                <div className="flex justify-start">
                  <div className="max-w-[80%] rounded-lg px-4 py-2 bg-muted">
                    <p className="text-sm whitespace-pre-wrap">{streamingContent}</p>
                    <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-1" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Attached Images Preview */}
            {supportsVision && attachedImages.length > 0 && (
              <div className="flex flex-wrap gap-2 p-2 border rounded-lg bg-muted/30">
                {attachedImages.map((img, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={img}
                      alt={`Attachment ${index + 1}`}
                      className="h-16 w-16 object-cover rounded"
                    />
                    <button
                      onClick={() => removeAttachedImage(index)}
                      className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="flex gap-2">
              <div className="flex-1 space-y-2">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={supportsVision ? "Type your message... (you can attach images)" : "Type your message... (Shift+Enter for new line)"}
                  disabled={isLoading}
                  className="min-h-[80px] resize-none"
                />
              </div>
              <div className="flex flex-col gap-2">
                {/* Image attachment button - only show if vision is supported */}
                {supportsVision && (
                  <>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => imageInputRef.current?.click()}
                      disabled={isLoading}
                      title="Attach image"
                    >
                      <ImagePlus className="h-4 w-4" />
                    </Button>
                  </>
                )}
                {isLoading ? (
                  <Button variant="destructive" onClick={handleStop} className="flex-1">
                    <StopCircle className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button onClick={handleSubmit} disabled={!input.trim()} className="flex-1">
                    <Send className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <Alert className="mt-6">
        <Info className="h-4 w-4" />
        <AlertDescription>
          <div className="space-y-1">
            <p><strong>Endpoint:</strong> <code className="text-xs bg-muted px-1 rounded">{chatCompletionsEndpoint}</code></p>
            <p className="text-sm text-muted-foreground">
              Make sure your vLLM server is running and listening on port {port}.
              The proxy supports ports 3000-4000.
            </p>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default VLLMPlayground;
